import { resolveEntitlement, type SubscriptionStateInput } from "@reactive-resume/utils";

export type SubscriptionLike = SubscriptionStateInput | null | undefined;

/**
 * "May they use paid features RIGHT NOW" — a single call into
 * `resolveEntitlement` (libs/utils/src/subscription-state.ts), which is now
 * the ONLY place that decides this (Codex follow-up review, 2026-09-21,
 * BLOCKER 2). Previously this function re-derived the ACTIVE/TRIALING/
 * CANCELED-grace logic itself in parallel with the helper, and five separate
 * server call sites each had their own slightly different version of the
 * same decision — an in-house trial past `lsCurrentPeriodEnd` reading as
 * FREE immediately (rather than waiting up to 24h for
 * `TrialService.expireFinishedTrials`'s daily 06:00 UTC sweep), a CANCELED
 * subscription still inside its paid Lemon-Squeezy-billed period reading as
 * paid, and a PAST_DUE subscription reading as FREE, are all now exactly one
 * rule table in one function. See that function's JSDoc for the full table.
 */
export const hasPaidPlan = (subscription?: SubscriptionLike): boolean =>
  resolveEntitlement(subscription).effectiveTier !== "FREE";

/** A settled, active Lemon Squeezy purchase; trials still qualify for checkout prompts. */
export const hasActivePaidSubscription = (subscription?: SubscriptionLike): boolean =>
  subscription?.status === "ACTIVE" &&
  (subscription.tier === "PRO" || subscription.tier === "ULTIMATE") &&
  Boolean(subscription.lsSubscriptionId);

/**
 * "Have they actually bought something" — a different question from
 * `hasPaidPlan`'s "may they use paid features". TRIALING must be true for
 * entitlement, so `hasPaidPlan` folds it in and is right to.
 *
 * Copy must key on THIS instead. Conflating the two is why settings told 55
 * trialing users "Renews {date}" on the very day their trial expires and
 * downgrades them, and why onboarding said "You're on Pro. All paid features
 * are unlocked." with no hint that a clock was running (prod, 2026-08-09).
 *
 * An expired-but-not-yet-swept trial is NOT trialing — `resolveEntitlement`
 * flips `isTrialing` to false the moment `lsCurrentPeriodEnd` passes.
 */
export const isTrialing = (subscription?: SubscriptionLike): boolean =>
  resolveEntitlement(subscription).isTrialing;

/** True once an in-house trial's `lsCurrentPeriodEnd` has passed but the row hasn't been swept yet. */
export const isTrialExpired = (subscription?: SubscriptionLike): boolean =>
  resolveEntitlement(subscription).trialExpired;

/** Whole days left in an active trial (ceil, min 1); `null` when not trialing. */
export const trialDaysLeft = (subscription?: SubscriptionLike): number | null =>
  resolveEntitlement(subscription).daysLeft;

export const hasUltimatePlan = (subscription?: SubscriptionLike): boolean =>
  resolveEntitlement(subscription).effectiveTier === "ULTIMATE" && hasPaidPlan(subscription);
